/**
 * The crew tag, and the card that opens from it.
 *
 * This is the smallest thing in the app and the one that appears most often:
 * a few characters beside a name, on every message, every roster row and
 * every profile. One component draws it everywhere - chat, member lists,
 * profiles, crew previews, voice controls - so a tag is the same tag
 * wherever you meet it.
 *
 * ## The artwork is the artwork
 *
 * Twelve SVG frames, drawn for Pugo, served from /tags. Not approximated in
 * CSS: an earlier version stacked gradients to imitate them, which is a
 * different picture wearing the same name.
 *
 * Each frame is a background image on one element, and the crew's letters are
 * real HTML text positioned in the frame's own text box. That split is the
 * whole design:
 *
 *   - the frame is a cached file, shared by every crew using that design,
 *     fetched once and reused from the browser's cache everywhere else. Two
 *     hundred tags on a screen are two hundred references to one image.
 *   - the letters stay text, so they are sharp at any zoom, selectable,
 *     searchable, translatable, and changing them is a string change rather
 *     than twelve re-exports. Nothing bakes a crew's name into artwork.
 *
 * The frame's aspect is fixed (240:72). A tag is sized by height and its
 * width follows, because stretching a drawn frame to fit longer text warps
 * the corners it was drawn with. Longer text gets a smaller letter instead.
 *
 * Cost, measured rather than claimed: one element, one background image, no
 * `filter`, no `will-change`, no animation unless somebody is pointing at it,
 * and at most twelve small files for the whole app - about 40KB, cached.
 *
 * ## Interaction
 *
 * Hovering opens a card about the crew. Clicking opens the crew. Those are
 * deliberately different from the name beside them, which opens the person -
 * and the tag stops the event so one press never does both. A tag drawn
 * inside a picker does neither, because there the tag is the thing being
 * chosen rather than a way somewhere else.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';
import { go } from '@/lib/hash';
import { useLocal } from '@/stores/local';
import { Icon } from '@/icons/Icon';
import { Tip } from './HoverCard';
import { absolute } from './user';

export type CrewTagFont = 'mono' | 'sans' | 'wide' | 'serif';

export type CrewDesign = {
  id: string;
  name: string;
  level: number;
  /** The frame, served from /tags. */
  url: string;
  /** What the letters should be over this plate. */
  ink: string;
  inkId?: string;
  /** The plate's mid colour, for a dot standing in for the whole tag. */
  swatch: string;
  aspect: number;
  textBox: { left: number; right: number; top: number; bottom: number };
  note?: string;
};

export type CrewChip = {
  id: string;
  tag: string;
  name: string;
  /** What the tag actually says. Defaults to `tag`. */
  text?: string;
  design?: CrewDesign | null;
  font?: CrewTagFont;
  motion?: boolean;
  /* Enough to draw the card without fetching the crew. */
  icon?: string | null;
  banner?: string | null;
  description?: string;
  memberCount?: number;
  level?: number;
};

/** Co-op, for a payload from a server that has not been updated yet. */
export const DEFAULT_DESIGN: CrewDesign = {
  id: 'co-op', name: 'Co-op', level: 1,
  url: '/tags/co-op.svg', ink: '#e6f3e2', swatch: '#203d34',
  aspect: 240 / 72,
  textBox: { left: 76 / 240, right: 14 / 240, top: 12 / 72, bottom: 12 / 72 }
};

const FONTS: Record<CrewTagFont, string> = {
  mono: 'font-mono',
  sans: '',
  wide: 'font-mono',
  serif: 'font-serif'
};

const TRACKING: Record<CrewTagFont, string> = {
  mono: '.04em',
  sans: '.02em',
  wide: '.16em',
  serif: '.03em'
};

/**
 * How tall a tag is drawn in each place it appears.
 *
 * 20-22px in a chat line, which is the size the brief asked for and the size
 * the frames were drawn at. Everything else is a multiple of the same frame.
 */
const HEIGHTS = { xs: 16, sm: 20, md: 24, lg: 34, xl: 48 } as const;
export type CrewTagSize = keyof typeof HEIGHTS;

/** The frame is one file per design, so the browser caches it once. */
function frameUrl(design: CrewDesign): string {
  return absolute(design.url);
}

/**
 * The tag beside a name.
 *
 * Everything but `size` comes from the crew, so the same tag is the same tag
 * in a chat row and in a 48px preview.
 */
export function CrewTag({ crew, className, size = 'sm', onOpen, still, title }: {
  crew?: CrewChip | null;
  className?: string;
  size?: CrewTagSize;
  /** Overrides the click. A picker passes one so choosing does not navigate. */
  onOpen?: () => void;
  /** No hover card, no navigation, no motion. For previews and pickers. */
  still?: boolean;
  /** Overrides the tooltip. */
  title?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  /*
   * The viewer's own preferences, read from the local store so turning
   * cosmetics off does not have to re-render the message list through a
   * context - only the tags subscribe to it.
   */
  const cosmetics = useLocal((s) => s.crewCosmetics);
  const hoverMotion = useLocal((s) => s.crewTagMotion);

  if (!crew) return null;

  const design = crew.design ?? DEFAULT_DESIGN;
  const text = (crew.text || crew.tag || '').toUpperCase();
  const height = HEIGHTS[size];
  const font = crew.font ?? 'mono';

  /* Cosmetics off: a plain, readable tag and nothing fetched. */
  if (!cosmetics) {
    return (
      <PlainTag
        crew={crew}
        text={text}
        height={height}
        className={className}
        onOpen={onOpen}
        still={still}
        title={title}
      />
    );
  }

  const width = Math.round(height * (design.aspect || DEFAULT_DESIGN.aspect));
  const box = design.textBox || DEFAULT_DESIGN.textBox;
  const boxWidth = width * (1 - box.left - box.right);
  const boxHeight = height * (1 - box.top - box.bottom);

  /*
   * The letter size.
   *
   * Fitted to the frame's own text box rather than chosen: eight characters
   * and three characters have to sit in the same drawn plate, and the frame
   * cannot stretch without warping the corners it was drawn with.
   */
  const perChar = boxWidth / Math.max(3, text.length || 3);
  const fontSize = Math.max(7, Math.min(boxHeight * 0.82, perChar * 1.55));

  const open = () => {
    if (still) return;
    if (onOpen) onOpen();
    else openCrewCard(crew.tag);
  };
  const mayMove = !still && hoverMotion && crew.motion !== false;

  const tag = (
    <span
      ref={ref}
      role={still && !onOpen ? undefined : 'button'}
      tabIndex={still && !onOpen ? undefined : 0}
      aria-label={crew.name + ' (' + text + ')'}
      data-crew-tag={crew.id}
      onClick={still && !onOpen ? undefined : (e) => { e.stopPropagation(); e.preventDefault(); open(); }}
      onKeyDown={still && !onOpen ? undefined : (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        e.stopPropagation();
        open();
      }}
      onMouseEnter={() => { if (!still && ref.current) hoverTag(crew, ref.current); }}
      onMouseLeave={() => { if (!still) unhoverTag(); }}
      onFocus={() => { if (!still && ref.current) hoverTag(crew, ref.current); }}
      onBlur={() => { if (!still) unhoverTag(); }}
      className={cn(
        'relative inline-flex flex-none select-none items-center justify-start align-middle outline-none',
        still && !onOpen ? '' : 'cursor-pointer focus-visible:shadow-[0_0_0_2px_var(--accent)] focus-visible:rounded-[4px]',
        mayMove && 'transition-transform duration-[200ms] ease-[var(--ease-out)] hover:-translate-y-px',
        className
      )}
      style={{
        width,
        height,
        backgroundImage: 'url(' + frameUrl(design) + ')',
        backgroundSize: '100% 100%',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <span
        className={cn('absolute flex items-center justify-center font-bold whitespace-nowrap', FONTS[font])}
        style={{
          left: box.left * width,
          right: box.right * width,
          top: box.top * height,
          bottom: box.bottom * height,
          fontSize,
          lineHeight: 1,
          letterSpacing: TRACKING[font],
          color: design.ink
        }}
      >
        {text}
      </span>
    </span>
  );

  /* The app's own tooltip, never the browser's grey box. */
  return <Tip text={title ?? crew.name} sub={still ? undefined : 'Click to open the crew'}>{tag}</Tip>;
}

/**
 * The tag with the decoration turned off.
 *
 * Deliberately not a stripped-down version of the drawn one: it is a plain
 * chip that reads clearly, fetches nothing, and cannot move. The crew keeps
 * its design - this is only what this viewer sees.
 */
function PlainTag({ crew, text, height, className, onOpen, still, title }: {
  crew: CrewChip;
  text: string;
  height: number;
  className?: string;
  onOpen?: () => void;
  still?: boolean;
  title?: string;
}) {
  const open = () => {
    if (still) return;
    if (onOpen) onOpen();
    else openCrewCard(crew.tag);
  };
  return (
    <Tip text={title ?? crew.name} sub={still ? undefined : 'Click to open the crew'}>
      <span
        role={still && !onOpen ? undefined : 'button'}
        tabIndex={still && !onOpen ? undefined : 0}
        aria-label={crew.name + ' (' + text + ')'}
        data-crew-tag={crew.id}
        onClick={still && !onOpen ? undefined : (e) => { e.stopPropagation(); e.preventDefault(); open(); }}
        onKeyDown={still && !onOpen ? undefined : (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault(); e.stopPropagation(); open();
        }}
        className={cn(
          'inline-flex flex-none items-center rounded-[calc(6px*var(--radius-mul,1))] px-1.5 align-middle',
          'font-mono font-bold text-dim shadow-[inset_0_0_0_1px_var(--line)]',
          still && !onOpen ? '' : 'cursor-pointer hover:text-ink',
          className
        )}
        style={{ height, lineHeight: height + 'px', fontSize: Math.max(9, height * 0.52) }}
      >
        {text}
      </span>
    </Tip>
  );
}

/* ---------------------------------------------------------- the card */

/*
 * One card for the whole app.
 *
 * Every tag everywhere shares it: the tag reports that it is being pointed
 * at, and a single host draws the card beside whichever one that was. A card
 * per tag would be a component per chat row, which for a busy channel is
 * hundreds of them waiting to render something nobody has asked for.
 */
type Hovering = { crew: CrewChip; rect: DOMRect } | null;
let hovering: Hovering = null;
const watchers = new Set<() => void>();
let enterTimer: ReturnType<typeof setTimeout> | null = null;
let leaveTimer: ReturnType<typeof setTimeout> | null = null;
let overCard = false;

function announce() { watchers.forEach((fn) => fn()); }

function hoverTag(crew: CrewChip, el: HTMLElement): void {
  if (leaveTimer) { clearTimeout(leaveTimer); leaveTimer = null; }
  if (enterTimer) clearTimeout(enterTimer);
  /* A short wait, so dragging the pointer across a roster does not flash a
     card for every person in it. */
  enterTimer = setTimeout(() => {
    hovering = { crew, rect: el.getBoundingClientRect() };
    announce();
  }, 320);
}

function unhoverTag(): void {
  if (enterTimer) { clearTimeout(enterTimer); enterTimer = null; }
  if (leaveTimer) clearTimeout(leaveTimer);
  leaveTimer = setTimeout(() => {
    if (overCard) return;
    hovering = null;
    announce();
  }, 180);
}

/** Closes whatever card is open. Called when a screen above it opens. */
export function closeCrewHover(): void {
  if (enterTimer) { clearTimeout(enterTimer); enterTimer = null; }
  if (leaveTimer) { clearTimeout(leaveTimer); leaveTimer = null; }
  overCard = false;
  if (hovering) { hovering = null; announce(); }
}

/**
 * Opens the crew, from a click on a tag.
 *
 * The preview card, not a route: a tag is usually clicked from inside a
 * conversation, and taking somebody out of the room they are reading to look
 * at four characters is the wrong trade. Wired from Crew.tsx, which owns the
 * preview - this file is imported by it, so it cannot import back.
 */
let opener: ((tag: string) => void) | null = null;
export function wireCrewOpener(fn: (tag: string) => void): void { opener = fn; }

export function openCrewCard(tag: string): void {
  closeCrewHover();
  if (opener) opener(tag);
  else go('crews');
}

export function CrewTagCardHost() {
  const [state, setState] = useState<Hovering>(hovering);

  useEffect(() => {
    const fn = () => setState(hovering);
    watchers.add(fn);
    return () => { watchers.delete(fn); };
  }, []);

  useEffect(() => {
    if (!state) return;
    const shut = () => closeCrewHover();
    window.addEventListener('scroll', shut, true);
    window.addEventListener('resize', shut);
    return () => {
      window.removeEventListener('scroll', shut, true);
      window.removeEventListener('resize', shut);
    };
  }, [state]);

  if (!state) return null;
  return <CrewHoverCard crew={state.crew} rect={state.rect} />;
}

const CARD_W = 320;

function CrewHoverCard({ crew, rect }: { crew: CrewChip; rect: DOMRect }) {
  const design = crew.design ?? DEFAULT_DESIGN;
  const banner = crew.banner ? absolute(crew.banner) : null;
  const icon = crew.icon ? absolute(crew.icon) : null;

  const margin = 12;
  let left = rect.left - 8;
  if (left + CARD_W > window.innerWidth - margin) left = window.innerWidth - CARD_W - margin;
  left = Math.max(margin, left);
  const height = 300;
  const below = window.innerHeight - rect.bottom > height + margin || rect.top < height + margin;
  const top = below ? rect.bottom + 8 : Math.max(margin, rect.top - height - 8);

  return createPortal(
    <div
      data-keeps-card
      role="dialog"
      aria-label={crew.name}
      onMouseEnter={() => { overCard = true; if (leaveTimer) { clearTimeout(leaveTimer); leaveTimer = null; } }}
      onMouseLeave={() => { overCard = false; unhoverTag(); }}
      style={{ left, top, width: CARD_W }}
      className={cn(
        'pugo-panel fixed z-[460] overflow-hidden rounded-[calc(18px*var(--radius-mul,1))] border border-line-soft bg-surface-1',
        'shadow-[var(--shadow-3)] animate-[cardin_180ms_var(--ease-out-soft)]'
      )}
    >
      <div
        className="relative h-[108px] overflow-hidden"
        style={{ background: banner ? 'var(--bg-0)' : design.swatch }}
      >
        {banner ? <img src={banner} alt="" className="h-full w-full object-cover" /> : null}
        <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[rgba(0,0,0,.45)] to-transparent" />
      </div>

      <div className="p-3.5">
        <div className="flex items-center gap-3">
          <span
            className="grid h-[46px] w-[46px] flex-none place-items-center overflow-hidden rounded-[calc(14px*var(--radius-mul,1))]"
            style={{ background: icon ? 'var(--bg-2)' : design.swatch, color: design.ink }}
          >
            {icon ? <img src={icon} alt="" className="h-full w-full object-cover" /> : <Icon name="users" size={22} />}
          </span>
          <div className="min-w-0 flex-1">
            <p className="m-0 truncate text-[16px] leading-tight font-bold text-ink">{crew.name}</p>
            <div className="mt-1 flex items-center gap-1.5">
              <span className="text-[12.5px] text-mute">Crew</span>
              <CrewTag crew={crew} size="sm" still />
            </div>
          </div>
        </div>

        {crew.description ? (
          <p className="mt-2.5 mb-0 line-clamp-2 text-[13px] leading-snug text-dim">{crew.description}</p>
        ) : null}

        <div className="mt-3 grid grid-cols-2 gap-2">
          <Fact icon="users" label={(crew.memberCount ?? 0) + (crew.memberCount === 1 ? ' member' : ' members')} />
          <Fact icon="chart" label={'Level ' + (crew.level ?? 1)} />
        </div>

        <button
          type="button"
          onClick={() => openCrewCard(crew.tag)}
          className="mt-3 flex h-[38px] w-full items-center justify-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-accent text-[13.5px] font-bold text-accent-fg transition-[background-color] duration-150 hover:bg-accent-hi"
        >
          View crew
          <Icon name="arrowR" size={15} />
        </button>
      </div>
    </div>,
    document.body
  );
}

function Fact({ icon, label }: { icon: string; label: string }) {
  return (
    <span className="flex items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 px-2.5 py-2 text-[12.5px] font-semibold text-dim">
      <Icon name={icon} size={14} className="flex-none text-mute" />
      <span className="truncate">{label}</span>
    </span>
  );
}
